/**
 * @file usePubSub.jsx
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Feb 14, 2026 10:24
 */
import axiosClient from 'src/services/axiosClient.js';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { WebPubSubClient } from '@azure/web-pubsub-client';

import { objectExists, objectHas, stringExists } from 'src/@common/utilities';

import { logger } from 'src/services/logger';
import { connectedSet } from 'src/store/actions/global.actions';

export const usePubSub = () => {
    const dispatch = useDispatch();

    const clientRef = useRef(null);

    // start: redux state --------------------------
    const authState = useSelector((state) => {
        return state.auth;
    });

    const { authenticated } = authState;
    // end: redux state -----------------------------

    const [error, setError] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [messageObj, setMessageObj] = useState(null);

    const connect = useCallback(async () => {
        let url = null;
        let client = null;

        if (clientRef.current) {
            clientRef.current?.stop();
        }

        if (authenticated === true) {

            try {

                const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

                if (stringExists(baseUrl)) {

                    const config = {
                        method: 'post',
                        baseURL: baseUrl,
                        url: '/pubSub/negotiate',
                    };

                    const response = await axiosClient(config);

                    if (objectExists(response)) {

                        if (objectHas(response, 'data.payload.url') &&
                            stringExists(response.data.payload.url)) {

                            url = response.data.payload.url;

                            client = new WebPubSubClient(url);

                            if (objectExists(client)) {

                                clientRef.current = client;

                                // Handle the connected event
                                client.on("connected", () => {
                                    setIsConnected(true);
                                    setError(null);
                                    dispatch(connectedSet(true));
                                });

                                // Handle the disconnected event
                                client.on("disconnected", () => {
                                    dispatch(connectedSet(false));
                                });

                                client.on("stopped", () => {
                                    dispatch(connectedSet(false));
                                });

                                client.on("group-message", (e) => {
                                    setMessageObj(e.message.data.message)
                                });

                                client.on("server-message", (e) => {
                                    setMessageObj(e.message.data)
                                });

                                client.start();

                                // join the group for the company
                                await client.joinGroup('veriome');

                            } else {
                                logger.error('pubSub: webPubSubClient was not created');
                                setError('Connection creation error');
                            }

                        } else {
                            logger.error('missing: response.data.payload.url');
                            setError('Connection creation error');
                        }

                    } else {
                        logger.error('usePubSub.negotiate.call: no response');
                        setError('Connection creation error');
                    }

                } else {
                    logger.error('missing: VITE_APP_API_BASE_URL');
                    setError('Connection creation error');
                }

            } catch (err) {
                logger.error('error calling: /pubSub/negotiate', err);
                console.error('err', err)
            }

        }


    }, [authenticated, dispatch]);

    const disconnect = useCallback(() => {
        if (clientRef.current) {
            clientRef.current?.stop();
            clientRef.current = null;
        }

        setIsConnected(false);
    }, []);


    // connect on mount, disconnect on unmount
    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    return {
        messageObj,     // Latest received data
        error,          // Error message
        isConnected,    // Connection status
    };
};
